/**
 * Where to go after a sign-in that interrupted work (launch decision 12 — graceful expiry):
 * when the platform token expires mid-session the API answers 401, the SPA drops the session
 * and offers to sign in again; the plan that was open rides sessionStorage across the redirect
 * round-trip so the user lands back in it, not on the home screen. This is the web platform's
 * store (`src/platform/web.ts`); the native shells keep the target with their pending login.
 */
import type { ReturnTo } from '../platform/platform';

export type { ReturnTo };

const KEY = 'planlets.returnTo';

/** Remembers the plan to reopen once the next sign-in completes. */
export function rememberReturnTo(target: ReturnTo): void {
  sessionStorage.setItem(KEY, JSON.stringify(target));
}

/** Takes (and forgets) the remembered plan, or null when the sign-in was not a resumption. */
export function takeReturnTo(): ReturnTo | null {
  const raw = sessionStorage.getItem(KEY);
  sessionStorage.removeItem(KEY);
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ReturnTo>;
    return typeof parsed.planId === 'string'
      ? { planId: parsed.planId, name: typeof parsed.name === 'string' ? parsed.name : null }
      : null;
  } catch {
    return null;
  }
}
